import ErrorImage from '@assets/illustrations/error.svg'
import type { AvailTypesAutocompleteProps } from '@entities/avail/dtos/types'
import { useEffect } from 'react'
import { FaRegEnvelope } from 'react-icons/fa'

import { CacheAdapter } from '@/shared/infra'
import { fDate, fDateTime } from '@/shared/utils'

interface ErrorFallbackProps {
  error: Error
  eventId?: string
}

function ErrorFallback({ error, eventId }: ErrorFallbackProps): JSX.Element {
  const user = CacheAdapter.get('userData')
  const destinationOrHotel: AvailTypesAutocompleteProps | undefined =
    CacheAdapter.get('destinationOrHotel')
  const searchingQuery = CacheAdapter.get('searchingQuery')
  const mailSuport = 'b2b.booking@b2breservas.com.br'
  const currentDate = new Date()

  useEffect(() => {
    try {
      import('@/shared/utils/sentry-custom-error').then(
        ({ captureCustomError }) => {
          captureCustomError({
            message: `React Error Boundary: ${error.message}`,
            error,
            level: 'fatal',
            user: user?.attributes
              ? {
                  id: user.attributes.sub,
                  username: user.attributes.name,
                  email: user.attributes.email,
                }
              : undefined,
            route: window.location.pathname,
            extraTags: {
              errorBoundary: true,
              hasSearchQuery: !!searchingQuery,
              hasDestination: !!destinationOrHotel,
              errorType: 'react_component',
              timestamp: new Date().toISOString(),
            },
            extra: {
              searchingQuery,
              destinationOrHotel,
              currentUrl: window.location.href,
              eventId,
              errorStack: error.stack,
              userAgent: navigator.userAgent,
              browserInfo: {
                language: navigator.language,
                platform: navigator.platform,
                cookieEnabled: navigator.cookieEnabled,
              },
            },
          })
        },
      )
    } catch (sentryError) {
      console.warn('Sentry error boundary logging failed:', sentryError)
    }
  }, [error, user, searchingQuery, destinationOrHotel, eventId])

  const emailBody = `
      ID do evento: ${eventId || 'Não disponível'}
      Data e hora: ${fDateTime(currentDate)}
      Ocorrência:
      ${error.message}
      ${error.name && `Nome do erro: ${error.name}`}
      Dados do usuário:
      ${user?.attributes.email && `E-mail: ${user?.attributes.email}`}
      ${user?.attributes.name && `Nome: ${user?.attributes.name}`}
      Destino:
      ${destinationOrHotel ? destinationOrHotel?.label && `Nome: ${destinationOrHotel?.label}` : 'Não encontrado'}
      ${destinationOrHotel ? destinationOrHotel?.value && `Valor: ${destinationOrHotel?.value}` : 'Não encontrado'}
      Dados da pesquisa de disponibilidade:
      ${searchingQuery?.companyId && `Posto id: ${searchingQuery?.companyId}`}
      ${searchingQuery?.company?.label && `Posto nome: ${searchingQuery?.company?.label}`}
      ${searchingQuery?.checkinDate && `Checkin: ${fDate(searchingQuery?.checkinDate)}`}
      ${searchingQuery?.checkoutDate && `Checkout: ${fDate(searchingQuery?.checkoutDate)}`}
      Página: ${window.location.href}
    `

  const mailtoLink = `mailto:${mailSuport}?subject=Erro%20inesperado%20B2B%20booking&body=${encodeURIComponent(
    emailBody,
  )}`

  const sendErrorReport = () => {
    window.location.replace(mailtoLink)
    window.location.reload()
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-[#273472] text-white">
      <div className="bg-[#4a5a9e] border border-[#5c73b6] p-6 rounded-lg shadow-md w-full max-w-md">
        <div className="flex flex-col items-center">
          <img
            src={ErrorImage}
            alt="Error illustration"
            className="w-80 mb-4"
          />
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">
              Oops! Aconteceu um erro.
            </h2>
            <p className="mb-4">
              Estamos trabalhando para corrigir isso o mais rápido possível.
            </p>
            <details className="bg-[#5c73b6] p-4 rounded-md shadow-sm">
              <summary className="font-medium cursor-pointer">
                Detalhes do erro
              </summary>
              <pre className="text-sm whitespace-pre-wrap break-words">
                <b>Data e hora: </b>
                {fDateTime(currentDate)}
                <br />
                {error?.message}
                <br />
                <b>Nome do erro: </b>
                <br />
                {error.name && (
                  <>
                    {error.name}
                    <br />
                  </>
                )}
                <br />a<b>Dados do usuário:</b>
                <br />
                {user?.attributes && (
                  <>
                    <b>Email: </b>
                    {user.attributes.email}
                    <br />
                    <b>Nome: </b>
                    {user.attributes.name}
                    <br />
                  </>
                )}
                {destinationOrHotel?.label && (
                  <>
                    <b>Destino: </b>
                    <br />
                    <b>Nome: </b>
                    {destinationOrHotel?.label}
                    <br />
                    <b>Valor: </b>
                    {destinationOrHotel?.value}
                    <br />
                  </>
                )}
                <br />
                {searchingQuery?.companyId && (
                  <>
                    <b>Dados da pesquisa de disponibilidade:</b>
                    <br />
                    <b>Posto id: </b>
                    {searchingQuery?.companyId}
                    <br />
                    {searchingQuery?.company && (
                      <>
                        <b>Posto nome: </b>
                        {searchingQuery?.company?.label}
                        <br />
                      </>
                    )}

                    {searchingQuery?.checkinDate && (
                      <>
                        <b>Checkin: </b>
                        {fDate(searchingQuery?.checkinDate)}
                        <br />
                      </>
                    )}

                    {searchingQuery?.checkoutDate && (
                      <>
                        <b>Checkout: </b>
                        {fDate(searchingQuery?.checkoutDate)}
                        <br />
                      </>
                    )}
                  </>
                )}
              </pre>
            </details>
            <button
              className="bg-[#7190d1] hover:bg-[#8ba6d8] text-white font-bold py-2 px-4 justify-center mt-4 w-full rounded flex items-center"
              onClick={sendErrorReport}
            >
              <FaRegEnvelope className="mr-2" />
              Enviar relatório de erro e recarregar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ErrorFallback
